import { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router';
import { api, errorMessage } from '../../shared/api/client';
import type { Order } from '../../shared/api/types';
import { Alert, Button, Card } from '../../shared/ui/components';
import { useAsync } from '../../shared/ui/useAsync';
import { usePolling } from '../presentation/usePolling';
import { useMe } from '../session/SessionProvider';
import { NEXT_STEP } from './labels';

/** Campanita corta con el navegador (sin archivos de audio). */
function chime(high = false) {
  try {
    const ctx = new (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)();
    [0, 0.18].forEach((delay, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.frequency.value = (high ? 1175 : 880) * (i ? 1.26 : 1);
      gain.gain.setValueAtTime(0.0001, ctx.currentTime + delay);
      gain.gain.exponentialRampToValueAtTime(0.4, ctx.currentTime + delay + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + delay + 0.35);
      osc.connect(gain).connect(ctx.destination);
      osc.start(ctx.currentTime + delay);
      osc.stop(ctx.currentTime + delay + 0.4);
    });
    setTimeout(() => void ctx.close(), 1000);
  } catch {
    /* sin audio */
  }
}

/** Suena cuando aparecen claves nuevas (no la primera vez que se carga). */
function useChimeOnNew(keys: string[] | null, on: boolean, high = false) {
  const seen = useRef<Set<string> | null>(null);
  useEffect(() => {
    if (!keys) return;
    const fresh = seen.current ? keys.some((k) => !seen.current!.has(k)) : false;
    seen.current = new Set(keys);
    if (fresh && on) chime(high);
  }, [keys?.join(','), on, high]); // eslint-disable-line react-hooks/exhaustive-deps
}

const minutesSince = (iso: string, now: number) => Math.max(0, Math.floor((now - new Date(iso).getTime()) / 60_000));

const COLUMNS: { key: Order['fulfillment']; title: string }[] = [
  { key: 'pending', title: 'Nuevos' },
  { key: 'preparing', title: 'Preparando' },
  { key: 'ready', title: 'Listos para entregar' },
];

/**
 * Pantalla de cocina: pedidos de los turnos abiertos en tres columnas, se actualiza sola cada
 * 3 segundos y suena cuando llega uno nuevo. Un toque avanza el pedido al siguiente paso.
 */
export function KitchenPage() {
  const me = useMe();
  const churchId = me.activeChurchId ?? '';
  const state = usePolling((signal) => api.get<{ items: Order[] }>(`/commerce/churches/${churchId}/orders`, signal).then((r) => r.items), 3000, [churchId]);
  const [sound, setSound] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(t);
  }, []);
  const live = (state.data ?? []).filter((o) => o.status === 'paid' && o.fulfillment !== 'delivered').sort((a, b) => a.createdAt.localeCompare(b.createdAt) || a.number - b.number);
  useChimeOnNew(state.data ? live.filter((o) => o.fulfillment === 'pending').map((o) => o.id) : null, sound);

  async function advance(o: Order) {
    const next = NEXT_STEP[o.fulfillment];
    if (!next) return;
    setBusy(o.id);
    setError(null);
    try {
      await api.post(`/commerce/churches/${churchId}/orders/${o.id}/fulfillment`, { to: next.to });
      state.refresh();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="kds">
      <header className="kds__bar">
        <Link to="/app/comercio?vista=cocina" className="kds__back">
          ← Cafetería y tienda
        </Link>
        <h1>Cocina</h1>
        <label className="kds__toggle">
          <input type="checkbox" checked={sound} onChange={(e) => (setSound(e.target.checked), e.target.checked && chime())} /> Sonido
        </label>
        <Button variant="secondary" onClick={() => void document.documentElement.requestFullscreen?.().catch(() => undefined)}>
          Pantalla completa
        </Button>
      </header>
      {error ? <Alert>{error}</Alert> : null}
      {state.error && !state.data ? <Alert>No se pudieron cargar los pedidos. {errorMessage(state.error)}</Alert> : null}
      <div className="kds__cols">
        {COLUMNS.map((col) => {
          const items = live.filter((o) => o.fulfillment === col.key);
          return (
            <section key={col.key} className={`kds__col kds__col--${col.key}`} aria-label={col.title}>
              <h2>
                {col.title} <span className="kds__count">{items.length}</span>
              </h2>
              {items.map((o) => {
                const mins = minutesSince(o.createdAt, now);
                const next = NEXT_STEP[o.fulfillment]!;
                return (
                  <button key={o.id} type="button" className={`kds__ticket${mins >= 10 && col.key !== 'ready' ? ' kds__ticket--late' : ''}`} disabled={busy === o.id} onClick={() => void advance(o)}>
                    <span className="kds__num">#{o.number}</span>
                    {o.customerName ? <span className="kds__name">{o.customerName}</span> : null}
                    <span className="kds__time">{mins === 0 ? 'ahora' : `${mins} min`}</span>
                    <ul>
                      {o.items.map((i) => (
                        <li key={i.productId}>
                          <strong>{i.quantity} ×</strong> {i.name}
                        </li>
                      ))}
                    </ul>
                    <span className="kds__next">{next.label} →</span>
                  </button>
                );
              })}
            </section>
          );
        })}
      </div>
    </div>
  );
}

type Turns = { preparing: { number: number; name: string | null }[]; ready: { number: number; name: string | null }[] };

/** Tele para clientes (sin sesión): qué números se preparan y cuáles ya pueden recogerse. */
export function TurnsDisplayPage() {
  const { token = '' } = useParams();
  const state = usePolling((signal) => api.get<Turns>(`/commerce/public/display/${token}`, signal), 3000, [token]);
  useChimeOnNew(state.data ? state.data.ready.map((t) => String(t.number)) : null, true, true);
  const turn = (t: { number: number; name: string | null }) => (
    <li key={t.number}>
      <span className="turns__num">{t.number}</span>
      {t.name ? <span className="turns__name">{t.name}</span> : null}
    </li>
  );
  if (state.error && !state.data) return <div className="turns turns--error">Esta pantalla ya no está activa.</div>;
  return (
    <div className="turns">
      <section className="turns__col">
        <h2>Preparando</h2>
        <ul>{state.data?.preparing.map(turn)}</ul>
      </section>
      <section className="turns__col turns__col--ready">
        <h2>¡Listo! Pasa por tu pedido</h2>
        <ul>{state.data?.ready.map(turn)}</ul>
      </section>
    </div>
  );
}

/** En Comercio → Pedidos: abrir la cocina y el enlace de la tele de turnos. */
export function KitchenLinks({ churchId, canManage }: { churchId: string; canManage: boolean }) {
  const link = useAsync((signal) => api.get<{ token: string | null }>(`/commerce/churches/${churchId}/display`, signal), [churchId]);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const token = link.status === 'ready' ? link.data.token : null;
  const url = token ? `${window.location.origin}/turnos/${token}` : '';
  const set = (on: boolean) =>
    api
      .put(`/commerce/churches/${churchId}/display`, { on })
      .then(() => link.reload())
      .catch((e) => setError(errorMessage(e)));
  return (
    <Card title="Pantallas">
      {error ? <Alert>{error}</Alert> : null}
      <div className="actions">
        <Link className="btn btn--primary" to="/app/cocina">
          Abrir pantalla de cocina
        </Link>
      </div>
      <p className="small muted">
        La pantalla de cocina muestra los pedidos en columnas, se actualiza sola y suena cuando llega uno nuevo. Úsala en una tablet o tele de la cocina.
      </p>
      {link.status === 'ready' ? (
        <>
          <p className="small">
            <strong>Tele de turnos para clientes:</strong> muestra los números en preparación y los listos para recoger. Ábrela en la tele del mostrador (no necesita iniciar sesión).
          </p>
          {token ? (
            <div className="code-row">
              <code className="code">{url}</code>
              <Button variant="secondary" onClick={() => void navigator.clipboard?.writeText(url).then(() => setCopied(true))}>
                {copied ? 'Copiado' : 'Copiar'}
              </Button>
              <a className="btn btn--ghost" href={url} target="_blank" rel="noreferrer">
                Abrir
              </a>
            </div>
          ) : null}
          {canManage ? (
            <Button variant={token ? 'ghost' : 'secondary'} onClick={() => void set(!token)}>
              {token ? 'Desactivar enlace de la tele' : 'Crear enlace para la tele de turnos'}
            </Button>
          ) : !token ? (
            <p className="small muted">La administración de la cafetería puede crear el enlace.</p>
          ) : null}
        </>
      ) : null}
    </Card>
  );
}
